import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  AlertTriangle, 
  Phone, 
  Heart, 
  Flame, 
  Anchor,
  Shield,
  MapPin,
  Clock,
  Navigation,
  Building2,
  Cross,
  FileText,
  ChevronRight,
  Info,
  ExternalLink
} from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";

// Emergency numbers data
const EMERGENCY_NUMBERS = [
  {
    number: "112",
    icon: AlertTriangle,
    color: "red",
    bgColor: "bg-red-500",
    borderColor: "border-red-200",
    textColor: "text-red-700",
    label: { en: "Universal Emergency", it: "Emergenza Universale" },
    description: { en: "Call for any emergency", it: "Chiama per qualsiasi emergenza" },
    priority: 1
  },
  {
    number: "118",
    icon: Heart,
    color: "rose",
    bgColor: "bg-rose-500",
    borderColor: "border-rose-200",
    textColor: "text-rose-700",
    label: { en: "Medical Emergency", it: "Emergenza Medica" },
    description: { en: "Ambulance / Medical help", it: "Ambulanza / Soccorso medico" },
    priority: 2
  },
  {
    number: "115",
    icon: Flame,
    color: "orange",
    bgColor: "bg-orange-500",
    borderColor: "border-orange-200",
    textColor: "text-orange-700",
    label: { en: "Fire Brigade", it: "Vigili del Fuoco" },
    description: { en: "Fire emergency", it: "Emergenza incendio" },
    priority: 3
  },
  {
    number: "1530",
    icon: Anchor,
    color: "blue",
    bgColor: "bg-blue-500",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
    label: { en: "Coast Guard", it: "Guardia Costiera" },
    description: { en: "Sea / Coast emergency", it: "Emergenza mare / costa" },
    priority: 4
  },
  {
    number: "113",
    icon: Shield,
    color: "slate",
    bgColor: "bg-slate-600",
    borderColor: "border-slate-200",
    textColor: "text-slate-700",
    label: { en: "Police", it: "Polizia" },
    description: { en: "Police assistance", it: "Assistenza polizia" },
    priority: 5
  }
];

// Location-specific medical data
const MEDICAL_DATA = {
  monopoli: {
    hospital: {
      name: "Ospedale San Giacomo",
      address: "Via Ospedale, 70043 Monopoli BA",
      phone: "+39 080 414 1111",
      mapsUrl: "https://maps.google.com/?q=Ospedale+San+Giacomo+Monopoli",
      type: { en: "General Hospital", it: "Ospedale Generale" }
    },
    urgentCare: {
      name: "Guardia Medica Monopoli",
      address: "Via Roma 25, 70043 Monopoli BA",
      phone: "+39 080 414 2222",
      mapsUrl: "https://maps.google.com/?q=Guardia+Medica+Monopoli",
      hours: { en: "8PM - 8AM + weekends", it: "20:00 - 08:00 + weekend" },
      type: { en: "After-hours Medical Care", it: "Assistenza Medica Notturna" }
    },
    pharmacy: {
      name: "Farmacia Centrale Monopoli",
      address: "Piazza Vittorio Emanuele II, Monopoli",
      phone: "+39 080 937 1234",
      mapsUrl: "https://maps.google.com/?q=Farmacia+Centrale+Monopoli",
      turnoUrl: "https://www.farmacieaperte.it/puglia/monopoli"
    },
    police: {
      name: "Carabinieri Monopoli",
      address: "Via Padre Kolbe, 70043 Monopoli BA",
      phone: "+39 080 803 0000",
      mapsUrl: "https://maps.google.com/?q=Carabinieri+Monopoli"
    }
  },
  fasano: {
    hospital: {
      name: "Ospedale Perrino",
      address: "SS 7 Mesagne, 72100 Brindisi BR",
      phone: "+39 0831 537 111",
      mapsUrl: "https://maps.google.com/?q=Ospedale+Perrino+Brindisi",
      type: { en: "Regional Hospital (25 min)", it: "Ospedale Regionale (25 min)" },
      note: { en: "Nearest major hospital", it: "Ospedale principale più vicino" }
    },
    urgentCare: {
      name: "Guardia Medica Fasano",
      address: "Via Roma 50, 72015 Fasano BR",
      phone: "+39 080 439 4444",
      mapsUrl: "https://maps.google.com/?q=Guardia+Medica+Fasano",
      hours: { en: "8PM - 8AM + weekends", it: "20:00 - 08:00 + weekend" },
      type: { en: "After-hours Medical Care", it: "Assistenza Medica Notturna" }
    },
    pharmacy: {
      name: "Farmacia del Corso Fasano",
      address: "Corso Vittorio Emanuele, Fasano",
      phone: "+39 080 441 5678",
      mapsUrl: "https://maps.google.com/?q=Farmacia+Fasano",
      turnoUrl: "https://www.farmacieaperte.it/puglia/fasano"
    },
    police: {
      name: "Carabinieri Fasano",
      address: "Piazza Ciaia, 72015 Fasano BR",
      phone: "+39 080 439 1111",
      mapsUrl: "https://maps.google.com/?q=Carabinieri+Fasano"
    }
  }
};

export default function EmergencyPage() {
  const { language } = useLanguage();
  const { location, locationData } = useLocation();
  const [expandedSection, setExpandedSection] = useState(null);

  const txt = {
    en: {
      title: "Emergency & Useful Contacts",
      subtitle: "Quick access to help when you need it",
      emergencyNumbers: "Emergency Numbers",
      emergencyNote: "All emergency services are free and available 24/7",
      medicalHelp: "Medical Help Nearby",
      medicalHelpDesc: "Hospitals and urgent care in",
      hospital: "Hospital",
      urgentCare: "Urgent Care",
      getDirections: "Get Directions",
      call: "Call",
      hours: "Hours",
      pharmacies: "Pharmacies",
      pharmacyDesc: "Find open pharmacies near you",
      nearestPharmacy: "Nearest Pharmacy",
      findOpenPharmacy: "Find Open Pharmacy Now",
      farmaciaDiTurno: "Farmacia di Turno",
      safetyHelp: "Safety & Support",
      safetyDesc: "Police and additional assistance",
      police: "Local Police",
      lostPassport: "Lost Passport?",
      lostPassportDesc: "Contact your embassy or consulate immediately",
      findEmbassy: "Find Your Embassy",
      tips: "Good to Know",
      tip1: "112 works from any phone, even without a SIM card",
      tip2: "Pharmacies rotate 24h duty (farmacia di turno)",
      tip3: "Guardia Medica is free for tourists with EHIC card",
      tip4: "Save your accommodation address — you may need it",
      stayCalm: "Stay calm. Help is available.",
      viewAll: "View all"
    },
    it: {
      title: "Emergenza e Contatti Utili",
      subtitle: "Accesso rapido all'aiuto quando ne hai bisogno",
      emergencyNumbers: "Numeri di Emergenza",
      emergencyNote: "Tutti i servizi di emergenza sono gratuiti e disponibili 24/7",
      medicalHelp: "Assistenza Medica Vicino",
      medicalHelpDesc: "Ospedali e pronto soccorso a",
      hospital: "Ospedale",
      urgentCare: "Guardia Medica",
      getDirections: "Indicazioni",
      call: "Chiama",
      hours: "Orari",
      pharmacies: "Farmacie",
      pharmacyDesc: "Trova farmacie aperte vicino a te",
      nearestPharmacy: "Farmacia più vicina",
      findOpenPharmacy: "Trova Farmacia Aperta Ora",
      farmaciaDiTurno: "Farmacia di Turno",
      safetyHelp: "Sicurezza e Supporto",
      safetyDesc: "Polizia e assistenza aggiuntiva",
      police: "Polizia Locale",
      lostPassport: "Passaporto Smarrito?",
      lostPassportDesc: "Contatta immediatamente la tua ambasciata o consolato",
      findEmbassy: "Trova la Tua Ambasciata",
      tips: "Da Sapere",
      tip1: "Il 112 funziona da qualsiasi telefono, anche senza SIM",
      tip2: "Le farmacie hanno turni notturni (farmacia di turno)",
      tip3: "La Guardia Medica è gratuita per turisti con tessera TEAM",
      tip4: "Salva l'indirizzo del tuo alloggio — potrebbe servirti",
      stayCalm: "Mantieni la calma. L'aiuto è disponibile.",
      viewAll: "Vedi tutto"
    }
  };

  const t = txt[language] || txt.en;
  const medicalInfo = MEDICAL_DATA[location] || MEDICAL_DATA.monopoli;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="emergency-page">
      {/* Header */}
      <header className="bg-gradient-to-b from-red-600 to-red-700 text-white px-6 pt-8 pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <AlertTriangle size={22} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-serif">{t.title}</h1>
              <p className="text-red-200 text-sm">{t.subtitle}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 py-6 max-w-4xl mx-auto space-y-6 pb-24 md:pb-8">
        
        {/* Emergency Numbers Section */}
        <section aria-labelledby="emergency-numbers-heading">
          <h2 id="emergency-numbers-heading" className="text-lg font-semibold text-stone-900 mb-3 flex items-center gap-2">
            <Phone size={18} className="text-red-600" />
            {t.emergencyNumbers}
          </h2>
          
          <div className="space-y-3">
            {EMERGENCY_NUMBERS.map((item) => {
              const Icon = item.icon;
              const isPrimary = item.number === "112";
              
              return (
                <a
                  key={item.number}
                  href={`tel:${item.number}`}
                  className={`block rounded-2xl p-4 transition-all active:scale-[0.98] ${
                    isPrimary 
                      ? 'bg-red-50 border-2 border-red-300 hover:border-red-400' 
                      : 'bg-white border border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid={`emergency-${item.number}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 ${item.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon size={26} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold ${item.textColor}`}>{item.number}</span>
                        <span className="text-stone-600 font-medium text-sm truncate">
                          {item.label[language] || item.label.en}
                        </span>
                      </div>
                      <p className="text-stone-500 text-sm mt-0.5">
                        {item.description[language] || item.description.en}
                      </p>
                    </div>
                    <div className={`${item.bgColor} text-white px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-1.5`}>
                      <Phone size={14} />
                      {t.call}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
          
          <p className="text-xs text-stone-500 mt-3 flex items-center gap-1.5">
            <Info size={12} />
            {t.emergencyNote}
          </p>
        </section>

        {/* Medical Help Section */}
        <section aria-labelledby="medical-help-heading" className="pt-2">
          <h2 id="medical-help-heading" className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Cross size={18} className="text-rose-600" />
            {t.medicalHelp}
          </h2>
          <p className="text-sm text-stone-500 mb-4">{t.medicalHelpDesc} {locationData?.name}</p>
          
          <div className="space-y-3">
            {/* Hospital */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 size={20} className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-rose-600 font-semibold uppercase tracking-wide">{t.hospital}</p>
                  <h3 className="font-semibold text-stone-900">{medicalInfo.hospital.name}</h3>
                  <p className="text-sm text-stone-500 mt-0.5">{medicalInfo.hospital.address}</p>
                  {medicalInfo.hospital.note && (
                    <p className="text-xs text-amber-600 mt-1">{medicalInfo.hospital.note[language] || medicalInfo.hospital.note.en}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <a
                  href={medicalInfo.hospital.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Navigation size={14} />
                  {t.getDirections}
                </a>
                <a
                  href={`tel:${medicalInfo.hospital.phone}`}
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Phone size={14} />
                  {t.call}
                </a>
              </div>
            </div>

            {/* Urgent Care */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart size={20} className="text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide">{t.urgentCare}</p>
                  <h3 className="font-semibold text-stone-900">{medicalInfo.urgentCare.name}</h3>
                  <p className="text-sm text-stone-500 mt-0.5">{medicalInfo.urgentCare.address}</p>
                  <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                    <Clock size={11} />
                    {t.hours}: {medicalInfo.urgentCare.hours[language] || medicalInfo.urgentCare.hours.en}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <a
                  href={medicalInfo.urgentCare.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Navigation size={14} />
                  {t.getDirections}
                </a>
                <a
                  href={`tel:${medicalInfo.urgentCare.phone}`}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Phone size={14} />
                  {t.call}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Pharmacies Section */}
        <section aria-labelledby="pharmacies-heading" className="pt-2">
          <h2 id="pharmacies-heading" className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Cross size={18} className="text-emerald-600" />
            {t.pharmacies}
          </h2>
          <p className="text-sm text-stone-500 mb-4">{t.pharmacyDesc}</p>
          
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Cross size={20} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">{t.nearestPharmacy}</p>
                <h3 className="font-semibold text-stone-900">{medicalInfo.pharmacy.name}</h3>
                <p className="text-sm text-stone-500 mt-0.5">{medicalInfo.pharmacy.address}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <a
                href={medicalInfo.pharmacy.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <Navigation size={14} />
                {t.getDirections}
              </a>
              <a
                href={`tel:${medicalInfo.pharmacy.phone}`}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <Phone size={14} />
                {t.call}
              </a>
            </div>
          </div>

          {/* Farmacia di Turno */}
          <a
            href={medicalInfo.pharmacy.turnoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block bg-emerald-50 border border-emerald-200 rounded-2xl p-4 hover:bg-emerald-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <Clock size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-800">{t.findOpenPharmacy}</p>
                  <p className="text-sm text-emerald-600">{t.farmaciaDiTurno}</p>
                </div>
              </div>
              <ExternalLink size={18} className="text-emerald-600" />
            </div>
          </a>
        </section>

        {/* Safety & Support Section */}
        <section aria-labelledby="safety-heading" className="pt-2">
          <h2 id="safety-heading" className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Shield size={18} className="text-slate-600" />
            {t.safetyHelp}
          </h2>
          <p className="text-sm text-stone-500 mb-4">{t.safetyDesc}</p>
          
          <div className="space-y-3">
            {/* Local Police */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield size={20} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-600 font-semibold uppercase tracking-wide">{t.police}</p>
                  <h3 className="font-semibold text-stone-900">{medicalInfo.police.name}</h3>
                  <p className="text-sm text-stone-500 mt-0.5">{medicalInfo.police.address}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <a
                  href={medicalInfo.police.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-700 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Navigation size={14} />
                  {t.getDirections}
                </a>
                <a
                  href={`tel:${medicalInfo.police.phone}`}
                  className="flex-1 bg-slate-700 hover:bg-slate-800 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Phone size={14} />
                  {t.call}
                </a>
              </div>
            </div>

            {/* Lost Passport */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText size={20} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900">{t.lostPassport}</h3>
                  <p className="text-sm text-amber-700 mt-1">{t.lostPassportDesc}</p>
                </div>
              </div>
              <a
                href="https://www.google.com/search?q=embassy+consulate+Italy"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 block w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 rounded-xl text-sm font-medium text-center transition-colors flex items-center justify-center gap-1.5"
              >
                <ExternalLink size={14} />
                {t.findEmbassy}
              </a>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section aria-labelledby="tips-heading" className="pt-2">
          <div className="bg-stone-100 border border-stone-200 rounded-2xl p-5">
            <h2 id="tips-heading" className="font-semibold text-stone-800 flex items-center gap-2 mb-4">
              <Info size={18} className="text-stone-600" />
              {t.tips}
            </h2>
            <ul className="space-y-3">
              {[t.tip1, t.tip2, t.tip3, t.tip4].map((tip, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
                  <div className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-teal-600">{i + 1}</span>
                  </div>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Reassurance Message */}
        <div className="text-center py-4">
          <p className="text-stone-500 text-sm">{t.stayCalm}</p>
        </div>

      </div>
      <Footer />
    </div>
  );
}
